/**
 * Shared cache for analytics data
 * Used by both Dashboard and Analytics pages to avoid duplicate API calls
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const analyticsCache = new Map();

/**
 * Get cache key for a specific data type and days
 * @param {string} type - Type of data (summary, pageViews, popularPages, etc.)
 * @param {number} days - Number of days
 * @returns {string} Cache key
 */
const getCacheKey = (type, days = 7) => `analytics_${type}_${days}`;

/**
 * Get cached data if it exists and is still valid
 * @param {string} type - Type of data
 * @param {number} days - Number of days
 * @returns {any|null} Cached data or null if not found/expired
 */
export const getCachedData = (type, days = 7) => {
  const key = getCacheKey(type, days);
  const cached = analyticsCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

/**
 * Set data in cache
 * @param {string} type - Type of data
 * @param {number} days - Number of days
 * @param {any} data - Data to cache
 */
export const setCachedData = (type, days = 7, data) => {
  const key = getCacheKey(type, days);
  analyticsCache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

/**
 * Get all cached analytics data for a specific day range
 * Used by Analytics page to get multiple cached items at once
 * @param {number} days - Number of days
 * @returns {object|null} Cached data object with all types or null
 */
export const getCachedAnalyticsData = (days = 7) => {
  const cached = getCachedData('full', days);
  if (cached) {
    return cached;
  }
  
  // Try to build from individual caches
  const summary = getCachedData('summary', days);
  const pageViews = getCachedData('pageViews', days);
  const popularPages = getCachedData('popularPages', days);
  const countries = getCachedData('countries', days);
  const devices = getCachedData('devices', days);
  
  // Return full cache if all pieces exist
  if (summary && pageViews && popularPages && countries && devices) {
    return {
      summary,
      pageViews,
      popularPages,
      countries,
      devices,
    };
  }
  
  return null;
};

/**
 * Set all analytics data in cache at once
 * Used by Analytics page to cache all data together
 * @param {number} days - Number of days
 * @param {object} data - Data object with all analytics types
 */
export const setCachedAnalyticsData = (days = 7, data) => {
  setCachedData('full', days, data);
  
  // Also cache individual pieces for Dashboard to access
  if (data.summary) setCachedData('summary', days, data.summary);
  if (data.pageViews) setCachedData('pageViews', days, data.pageViews);
  if (data.popularPages) setCachedData('popularPages', days, data.popularPages);
  if (data.countries) setCachedData('countries', days, data.countries);
  if (data.devices) setCachedData('devices', days, data.devices);
};

